/**
 * Legacy cache migrator for the Inspection Series listing.
 *
 * Converts the old Kendo localStorage shape stored by index.jsx's saveCacheToLocalStorage:
 *   { hiddenColumns: string[], stateFilters: LegacyStateFilters, kendoSortSettings: KendoSort[], page: number }
 *
 * into a partial ListingPreferences<SeriesFilters> understood by useListingPreferences.
 *
 * Contract rules applied here (from the frozen contract §Preference Migration Rules):
 *  - Accept the { data: ... } envelope (handled by useListingPreferences).
 *  - Convert hiddenColumns → visibleColumnKeys using ALL_SERIES_COLUMNS minus hidden.
 *    sanitizeColumns (called by useListingPreferences) does the final policy cleanup.
 *  - Convert kendoSortSettings[0] → { field, direction } (ListingSort).
 *  - Convert stateFilters → SeriesFilters (migration of each field family).
 *  - page (1-indexed integer) is intentionally NOT restored per contract rule 4
 *    ("Convert legacy page shapes to one-indexed { number, size }; use the page's
 *    default size when absent or invalid" — page number is not restored, only pageSize).
 *  - Series has no pageSize in its cache (legacy stored page number only); use default 25.
 *  - Search is never read or written (Series has no search).
 *  - Invalid/unrecognizable shapes return null.
 *  - Stale column keys not in ALL_SERIES_COLUMNS are silently dropped by sanitizeColumns.
 *  - Required columns (id, actions) are always injected by sanitizeColumns.
 *
 * Scope-injected filter values (building in building scope, account in account scope)
 * are stripped from legacy stateFilters — scope is re-applied by the parent adapter
 * based on current scope props.
 */
import { INITIAL_SERIES_FILTERS, EXPIRED_SERIES_ALL_VALUE } from '../types';
import { ALL_SERIES_COLUMNS } from './seriesColumnPolicy';
function isKendoSortSettings(value) {
    if (!Array.isArray(value))
        return false;
    if (value.length === 0)
        return true;
    const first = value[0];
    return (first !== null &&
        typeof first === 'object' &&
        typeof first.field === 'string' &&
        typeof first.dir === 'string');
}
function isLegacySeriesCache(raw) {
    if (!raw || typeof raw !== 'object')
        return false;
    const obj = raw;
    // A valid legacy cache must have at least one of the known fields.
    // We require hiddenColumns or stateFilters to exist (both may be absent when
    // the cache was partially written), but reject anything that looks canonical.
    if ('version' in obj)
        return false; // canonical shape — handled upstream
    return 'hiddenColumns' in obj || 'stateFilters' in obj || 'kendoSortSettings' in obj;
}
// ---------------------------------------------------------------------------
// Value converters
// ---------------------------------------------------------------------------
/** Convert a legacy Kendo DateRangePicker state → SeriesDateRange (preserving rangeType). */
function migrateExpirationDate(raw) {
    if (!raw || typeof raw !== 'object')
        return { start: null, end: null, rangeType: null };
    const obj = raw;
    return {
        start: typeof obj.startDate === 'string' && obj.startDate ? obj.startDate : null,
        end: typeof obj.endDate === 'string' && obj.endDate ? obj.endDate : null,
        rangeType: typeof obj.rangeType === 'string' && obj.rangeType ? obj.rangeType : null
    };
}
/** Convert a legacy multi-select value (empty string | array) → number[]. */
function migrateNumberArray(raw) {
    if (raw == null || raw === '')
        return [];
    if (Array.isArray(raw))
        return raw.map(Number).filter((n) => !Number.isNaN(n));
    const n = Number(raw);
    return Number.isNaN(n) ? [] : [n];
}
/**
 * Convert a legacy BooleanSelect value ('' | 'true' | 'false' | boolean)
 * → boolean | null. Returns null (no filter) for empty/unknown.
 */
function migrateBooleanSelect(raw) {
    if (raw === 'true' || raw === true)
        return true;
    if (raw === 'false' || raw === false)
        return false;
    return null;
}
/**
 * Convert `expiredSeries` from the legacy BooleanSelect value.
 * Default in the old reducer was 'false'. Empty string was All.
 */
function migrateExpiredSeries(raw) {
    if (raw === 'true' || raw === true)
        return true;
    if (raw === '' || raw === EXPIRED_SERIES_ALL_VALUE)
        return null;
    return false;
}
// ---------------------------------------------------------------------------
// Main migrator
// ---------------------------------------------------------------------------
export const seriesLegacyMigrator = (raw) => {
    var _a, _b;
    if (!isLegacySeriesCache(raw))
        return null;
    const legacy = raw;
    const sf = (_a = legacy.stateFilters) !== null && _a !== void 0 ? _a : {};
    // ── Page size ──
    // Series legacy cache does not store pageSize — only the page number.
    // Fall back to the default (25), which useListingPreferences will use.
    const pageSize = INITIAL_SERIES_FILTERS.building !== undefined ? 25 : 25;
    // ── Sort ──
    let sort = null;
    const ks = legacy.kendoSortSettings;
    if (isKendoSortSettings(ks) && ks.length > 0) {
        const s = ks[0];
        sort = {
            field: s.field,
            direction: s.dir === 'asc' || s.dir === 'desc' ? s.dir : 'asc'
        };
    }
    // ── Filter values ──
    // Scope-injected values (building in building scope, account in account scope)
    // are intentionally NOT restored from the legacy cache — they are re-applied by
    // the parent adapter based on current scope props.
    const filters = Object.assign(Object.assign({}, INITIAL_SERIES_FILTERS), { building: [], account: [], territory: migrateNumberArray(sf.territory), expirationDate: migrateExpirationDate(sf.expirationDate), status: migrateBooleanSelect(sf.status), autoExtend: migrateBooleanSelect(sf.autoExtend), expiredSeries: migrateExpiredSeries(sf.expiredSeries) });
    // ── Visible columns ──
    // Convert hiddenColumns → visibleColumnKeys by taking ALL_SERIES_COLUMNS
    // minus hidden entries. sanitizeColumns (called by useListingPreferences)
    // enforces the final policy (allowed, required, ordering).
    const hiddenSet = new Set((_b = legacy.hiddenColumns) !== null && _b !== void 0 ? _b : []);
    const visibleColumnKeys = [...ALL_SERIES_COLUMNS].filter((col) => !hiddenSet.has(col));
    // Series filters are always-active (no activeFilterKeys toggle mechanism).
    // Return empty array — the preference hook will use this as-is, and the
    // filter config builder ignores activeFilterKeys for Series (task-01 adaptation #4).
    const activeFilterKeys = [];
    return { pageSize, sort, filters, activeFilterKeys, visibleColumnKeys };
};
